import React, { FC, useState } from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useCreateTaskMutation, useTransitionTaskMutation } from 'services/api/tasks.api';
import { TaskDraft, Task } from 'services/types/task';

interface Props {
  clientId: number;
  openTaskModal: boolean;
  openTaskTransitionModal: boolean;
  currentTask: Task | undefined;
  onCloseTaskModal: () => void;
  onCloseTaskTransitionModal: () => void;
  onTaskCreated: () => void;
  onTaskTransitioned: () => void;
}

const TaskModals: FC<Props> = ({
  clientId,
  openTaskModal,
  openTaskTransitionModal,
  currentTask,
  onCloseTaskModal,
  onCloseTaskTransitionModal,
  onTaskCreated,
  onTaskTransitioned,
}) => {
  const [taskName, setTaskName] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskGoal, setTaskGoal] = useState('');
  const [taskError, setTaskError] = useState<string | null>(null);

  const [createTaskMutation, { isLoading: isCreatingTask }] = useCreateTaskMutation();
  const [transitionTaskMutation, { isLoading: isTransitioningTask }] = useTransitionTaskMutation();

  const handleCreateTask = () => {
    if (!taskName.trim() || !taskDescription.trim() || !taskGoal.trim()) {
      setTaskError('Todos los campos son obligatorios');
      return;
    }

    const taskData: TaskDraft = {
      clientId,
      name: taskName.trim(),
      description: taskDescription.trim(),
      goal: taskGoal.trim(),
    };

    createTaskMutation(taskData)
      .then((response) => {
        if ('data' in response) {
          setTaskName('');
          setTaskDescription('');
          setTaskGoal('');
          setTaskError(null);
          onCloseTaskModal();
          onTaskCreated();
        }
        if ('error' in response) {
          setTaskError(`Error al crear la tarea. ${'data' in response.error ? String(response.error.data) : ''}`);
          console.error('Error al crear la tarea', response.error); // eslint-disable-line no-console
        }
      })
      .catch((err) => {
        setTaskError('Error al crear la tarea');
        console.error('Error al crear la tarea', err); // eslint-disable-line no-console
      });
  };

  const handleTransitionTask = (event: 'FINISH' | 'FAIL') => {
    if (!currentTask) return;

    transitionTaskMutation({ taskId: currentTask.id, event })
      .then((response) => {
        if ('data' in response) {
          onCloseTaskTransitionModal();
          onTaskTransitioned();
        }
        if ('error' in response) {
          console.error('Error al cambiar estado de la tarea', response.error); // eslint-disable-line no-console
        }
      })
      .catch((err) => {
        console.error('Error al cambiar estado de la tarea', err); // eslint-disable-line no-console
      });
  };

  const handleCloseTaskModal = () => {
    setTaskName('');
    setTaskDescription('');
    setTaskGoal('');
    setTaskError(null);
    onCloseTaskModal();
  };

  return (
    <>
      {/* Task Creation Modal */}
      <Dialog open={openTaskModal} onClose={handleCloseTaskModal} maxWidth="sm" fullWidth>
        <DialogTitle>Crear Nueva Tarea</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Nombre de la tarea"
              fullWidth
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              placeholder="Dale un nombre a la tarea, para poder identificarla. Ej: Obtener rut"
            />
            <TextField
              label="Descripción"
              fullWidth
              multiline
              rows={3}
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              placeholder="Describe qué debe hacer la IA y el porqué. Ejemplo: 'Obtener el rut del cliente para poder generar la cotización'"
            />
            <TextField
              label="Condición de fin"
              fullWidth
              multiline
              rows={2}
              value={taskGoal}
              onChange={(e) => setTaskGoal(e.target.value)}
              placeholder="Describe en que momento se debe finalizar la tarea. Ejemplo: 'Cuando se tenga el rut del cliente'"
            />
            {taskError && (
              <Typography color="error" variant="body2">
                {taskError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTaskModal}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateTask} disabled={isCreatingTask}>
            {isCreatingTask ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Crear Tarea'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Transition Modal */}
      <Dialog open={openTaskTransitionModal} onClose={onCloseTaskTransitionModal} maxWidth="sm" fullWidth>
        <DialogTitle>Tarea en Progreso</DialogTitle>
        <DialogContent>
          {currentTask && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {currentTask.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {currentTask.description}
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }}>
                <strong>Objetivo:</strong> {currentTask.goal}
              </Typography>
              <Chip label="EN PROGRESO" color="warning" size="small" sx={{ mt: 2 }} />
              <Typography variant="body2" sx={{ mt: 2 }}>
                ¿Cómo quieres finalizar esta tarea?
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseTaskTransitionModal}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleTransitionTask('FAIL')}
            disabled={isTransitioningTask}
            startIcon={<CloseIcon />}
          >
            {isTransitioningTask ? <CircularProgress size={20} /> : 'Marcar como Fallida'}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleTransitionTask('FINISH')}
            disabled={isTransitioningTask}
            startIcon={<CheckCircleIcon />}
          >
            {isTransitioningTask ? <CircularProgress size={20} /> : 'Completar Tarea'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TaskModals;
